import { ResearchCover } from './ResearchCover';
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import {
  getIndustries,
  type ResearchEntry,
} from '../../services/researchApi';

/*
 * ResearchInPracticeCard
 * ----------------------
 * A single tile inside the horizontally-scrolling "Research in
 * practice" slider on the home page. Deliberately compact and
 * poster-shaped so 3 fit into a normal desktop viewport width
 * without shrinking the copy to fine-print.
 *
 * The card is a <Link> targeting the detail page at
 * `/research/<sorting_id>` — clicking anywhere on the tile
 * (image, header, description, chips) routes there. When a
 * `sorting_id` is missing we fall back to the Mongo `id` string
 * so the route still resolves.
 *
 * Framing note: this tile isn't a product card. It's a proof-of-
 * expertise tile. Category chips ("Research" / "Implementation")
 * and the industry chip live at the bottom so the visitor gets
 * the "what this shows" signal without any "buy this" language.
 */

const CARD_FIXED_CLASSES =
  // shrink-0 keeps each tile at its intrinsic width inside the
  // horizontal scroll container instead of collapsing when there
  // are more tiles than fit.
  'shrink-0 snap-start ' +
  // Tile widths per breakpoint. On mobile a single card fills
  // ~85% of the viewport (leaves a peek of the next one — a
  // familiar "slider" affordance). On tablet ~2 cards, on
  // desktop ~3.
  'w-[85vw] sm:w-[360px] md:w-[380px] lg:w-[400px]';

export interface ResearchInPracticeCardProps {
  entry: ResearchEntry;
  /** Stagger the card's entrance animation inside the slider. */
  animationDelay?: number;
  animateEntrance?: boolean;
  duplicate?: boolean;
}

export const ResearchInPracticeCard: React.FC<ResearchInPracticeCardProps> = ({
  entry,
  animationDelay = 0,
  animateEntrance = true,
  duplicate = false,
}) => {
  // Prefer the human-friendly `sorting_id` for the detail-page URL
  // (`/research/10`) — clean and shareable. Fall back to the
  // stringified Mongo _id so the tile is still clickable even if a
  // row is missing `sorting_id`.
  const routeParam =
    typeof entry.sorting_id === 'number' ? String(entry.sorting_id) : entry.id;

  const primaryCategory = entry.categories?.[0];
  const secondaryCategory = entry.categories?.[1];
  const industries = getIndustries(entry);
  // Cap industry chips at 2 in the card view so a doc that spans 5+
  // verticals doesn't blow out the card's vertical rhythm. The
  // remaining industries are indicated with a "+N" chip and the
  // detail page shows the full list.
  const visibleIndustries = industries.slice(0, 2);
  const hiddenIndustryCount = Math.max(0, industries.length - 2);

  return (
    <motion.div
      initial={animateEntrance ? { opacity: 0, y: 16 } : false}
      aria-hidden={duplicate || undefined}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{
        duration: 0.45,
        delay: animationDelay,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={CARD_FIXED_CLASSES}
    >
      <Link
        to={`/research/${routeParam}`}
        tabIndex={duplicate ? -1 : undefined}
        className={
          'group relative flex h-full flex-col overflow-hidden rounded-2xl ' +
          'bg-white ring-1 ring-navy-100 ' +
          'shadow-[0_15px_40px_-15px_rgba(2,8,20,0.2)] ' +
          'hover:shadow-[0_25px_55px_-15px_rgba(2,8,20,0.3)] ' +
          'transition-shadow duration-300 ' +
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2'
        }
      >
        {/* Explicit media takes priority; missing/broken covers use the PDF first page. */}
        <div className="relative aspect-[16/9] overflow-hidden bg-navy-950 flex items-center justify-center p-6">
          <ResearchCover entry={entry} className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" />
          {/* Subtle top-right pip to hint this is a link */}
          <span
            aria-hidden
            className="absolute right-3 top-3 inline-flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm p-1.5 text-white/70 group-hover:text-white group-hover:bg-white/20 transition-colors"
          >
            <ArrowUpRight className="w-3.5 h-3.5" />
          </span>
        </div>

        {/* Text block */}
        <div className="flex-1 flex flex-col p-5">
          {entry.header && (
            <h3 className="text-[17px] md:text-[18px] font-bold text-navy-950 leading-snug mb-2 line-clamp-2 group-hover:text-accent-700 transition-colors">
              {entry.header}
            </h3>
          )}
          {entry.description && (
            <p className="text-sm text-navy-700 leading-relaxed line-clamp-4 mb-4">
              {entry.description}
            </p>
          )}

          {/* Chips row — pinned to the bottom via `mt-auto` so all
              cards have their chips aligned regardless of copy length. */}
          <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-2">
            {primaryCategory && (
              <span className="inline-flex items-center rounded-full bg-accent-50 text-accent-700 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 ring-1 ring-accent-200">
                {primaryCategory}
              </span>
            )}
            {secondaryCategory && (
              <span className="inline-flex items-center rounded-full bg-accent-50 text-accent-700 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 ring-1 ring-accent-200">
                {secondaryCategory}
              </span>
            )}
            {visibleIndustries.map((ind) => (
              <span
                key={ind}
                className="inline-flex items-center rounded-full bg-navy-50 text-navy-700 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 ring-1 ring-navy-200"
              >
                {ind}
              </span>
            ))}
            {hiddenIndustryCount > 0 && (
              <span
                title={industries.slice(2).join(', ')}
                className="inline-flex items-center rounded-full bg-navy-100 text-navy-700 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 ring-1 ring-navy-200"
              >
                +{hiddenIndustryCount}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
